/**
 * Client-side token storage utilities
 */

const TOKEN_KEY = 'cms_token';

/**
 * Set the CMS token in localStorage
 * 
 * IMPORTANT: Browser's native 'storage' event ONLY fires for changes in OTHER tabs/windows.
 * For same-tab updates to trigger storage listeners, we must manually dispatch the event.
 * We only dispatch if the token actually changed to minimize unnecessary events.
 */
export function setClientToken(token: string): void {
  if (typeof window !== 'undefined') {
    try {
      // Check if token actually changed to avoid unnecessary events and potential loops
      const currentToken = localStorage.getItem(TOKEN_KEY);
      const tokenChanged = currentToken !== token;

      localStorage.setItem(TOKEN_KEY, token);

      // Only dispatch if token actually changed
      // The storage event listeners have guards to prevent infinite loops:
      // - They check if token hasn't changed (client.ts line 261)
      // - They check if initialization is in progress (client.ts line 269)
      // - They check retry limits (client.ts line 277)
      if (tokenChanged) {
        window.dispatchEvent(new Event('storage'));
      }
    } catch (error) {
      console.error('Failed to save token to localStorage:', error);
    }
  }
}

/**
 * Get the CMS token from localStorage
 */
export function getClientToken(): string | null {
  if (typeof window !== 'undefined') {
    try {
      return localStorage.getItem(TOKEN_KEY);
    } catch (error) {
      console.error('Failed to get token from localStorage:', error);
      return null;
    }
  }
  return null;
}

/**
 * Clear the CMS token from localStorage
 */
export function clearClientToken(): void {
  if (typeof window !== 'undefined') {
    try {
      localStorage.removeItem(TOKEN_KEY);
    } catch (error) {
      console.error('Failed to clear token from localStorage:', error);
    }
  }
}

/**
 * Check if a token exists in localStorage
 */
export function hasClientToken(): boolean {
  return !!getClientToken();
}

